import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Archive,
  CaretDown,
  PencilSimple,
  Warning,
} from '@phosphor-icons/react';
import { useDataSidebarContext } from './context';
import { CustomColorPalette } from '../../../../theme';

interface SectionStyledProps {
  hasBorder: boolean;
}

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: prop => prop !== 'hasBorder',
})<SectionStyledProps>(({ theme, hasBorder }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      display: 'none',
    },
    marginBottom: hasBorder ? '8px' : '0px',
    borderBottom: hasBorder
      ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
      : 'none',
    '&.Mui-expanded': {
      margin: '0 0 8px 0',
    },
  };
});

const StyledAccordionSummary = styled(AccordionSummary)({
  minHeight: '32px',
  maxHeight: '32px',
  padding: '0 8px',
  '&.Mui-expanded': {
    minHeight: '32px',
    maxHeight: '32px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0 4px 0',
    overflow: 'hidden',
  },
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColorPalette.gray[500],
  fontFamily: 'Roboto',
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? palette.gray[200] : palette.gray[700],
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
    },
  };
});

const DeleteMenuItem = styled(MenuItem)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? '#ef4444' : '#dc2626',
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode
        ? 'rgba(239,68,68,0.1)'
        : 'rgba(220,38,38,0.1)',
    },
  };
});

const contextMenuPaperSx = (
  isDarkMode: boolean,
  palette: CustomColorPalette,
) => ({
  backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[50],
  border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`,
  borderRadius: '8px',
  minWidth: '160px',
  boxShadow: isDarkMode
    ? '0 4px 12px rgba(0,0,0,0.4)'
    : '0 4px 12px rgba(0,0,0,0.1)',
});

interface SectionProps {
  title: string;
  children: React.ReactNode;
  hasBorder?: boolean;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  isDisabled?: boolean;
  onArchive?: () => void;
  onEditSpace?: () => void;
}

export const Section: React.FC<SectionProps> = ({
  title,
  children,
  hasBorder = false,
  isAccordion = false,
  defaultExpanded = true,
  isDisabled = false,
  onArchive,
  onEditSpace,
}) => {
  const { isDarkMode, palette } = useDataSidebarContext();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const hasContextActions = !!onArchive || !!onEditSpace;

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!hasContextActions) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const contextMenuEl = hasContextActions ? (
    <Menu
      open={contextMenu !== null}
      onClose={handleCloseContextMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
      slotProps={{
        paper: {
          sx: contextMenuPaperSx(isDarkMode, palette),
        },
      }}
    >
      {onEditSpace && (
        <StyledMenuItem
          onClick={() => {
            handleCloseContextMenu();
            onEditSpace();
          }}
        >
          <PencilSimple size={16} />
          Edit Space
        </StyledMenuItem>
      )}
      {onArchive && !isDisabled && (
        <DeleteMenuItem
          onClick={() => {
            handleCloseContextMenu();
            onArchive();
          }}
        >
          <Archive size={16} />
          Archive
        </DeleteMenuItem>
      )}
    </Menu>
  ) : null;

  if (isAccordion) {
    return (
      <>
        <StyledAccordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          hasBorder={hasBorder}
        >
          <StyledAccordionSummary
            onContextMenu={handleContextMenu}
            expandIcon={
              <CaretDown
                size={16}
                color={palette.gray[500]}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
                maxWidth: '100%',
              }}
            >
              {isDisabled && (
                <Tooltip
                  title="This space is disabled. Go to Settings to enable it."
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Warning size={14} color="#FFC107" weight="fill" />
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={title} arrow placement="top">
                <SectionTitle>{title}</SectionTitle>
              </Tooltip>
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails
            sx={{
              padding: '0 8px 8px 8px',
              paddingBottom: hasBorder ? '8px' : '8px',
            }}
          >
            {children}
          </AccordionDetails>
        </StyledAccordion>
        {contextMenuEl}
      </>
    );
  }

  return (
    <>
      <Box
        onContextMenu={handleContextMenu}
        sx={{
          paddingBottom: '8px',
          marginBottom: '8px',
          borderBottom: hasBorder
            ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
            : 'none',
        }}
      >
        <SectionTitle
          sx={{
            padding: '8px 8px 4px 8px',
          }}
        >
          {title}
        </SectionTitle>
        {children}
      </Box>
      {contextMenuEl}
    </>
  );
};
